import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, FortnoxInvoicePaymentAccountSettings as FortnoxInvoicePaymentAccountSettingsType } from '@/types';
import { apiService } from '@/utils/api';

interface FortnoxInvoicePaymentAccountSettingsProps {
  className?: string;
}

const FortnoxInvoicePaymentAccountSettings: React.FC<FortnoxInvoicePaymentAccountSettingsProps> = ({ className }) => {
  const queryClient = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<FortnoxInvoicePaymentAccountSettingsType>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Payment service provider options
  const paymentServiceProviders = [
    { text: 'Select One', value: '' },
    { text: 'Kustom', value: 'kustom' },
    { text: 'Ledyer', value: 'ledyer' }
  ];

  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery(
    ['sites'],
    () => apiService.getSites()
  );

  // Set default site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Fetch Fortnox invoice payment account settings
  const { data: invoicePaymentSettings, isLoading: settingsLoading } = useQuery(
    ['fortnox-invoice-payment-account-settings', selectedSiteId],
    () => apiService.getFortnoxInvoicePaymentAccountSettings(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Update form data when settings are loaded
  useEffect(() => {
    if (invoicePaymentSettings) {
      setFormData(invoicePaymentSettings);
    }
  }, [invoicePaymentSettings]);

  // Save mutation
  const saveMutation = useMutation(
    async (data: FortnoxInvoicePaymentAccountSettingsType) => {
      if (data.id) {
        return apiService.updateFortnoxInvoicePaymentAccountSettings(selectedSiteId!, data);
      } else {
        return apiService.createFortnoxInvoicePaymentAccountSettings(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Invoice Payment Account Settings saved' });
        queryClient.invalidateQueries(['fortnox-invoice-payment-account-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async () => {
      return apiService.deleteFortnoxInvoicePaymentAccountSettings(selectedSiteId!);
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Invoice Payment Account Settings deleted' });
        setFormData({});
        queryClient.invalidateQueries(['fortnox-invoice-payment-account-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSiteChange = (siteId: number) => {
    setSelectedSiteId(siteId);
    setFormData({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;

    const submitData: FortnoxInvoicePaymentAccountSettingsType = {
      ...formData,
      site_id: selectedSiteId,
    } as FortnoxInvoicePaymentAccountSettingsType;

    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const isLoading = sitesLoading || settingsLoading;

  return (
    <div className={`container mx-auto px-4 py-8 ${className || ''}`}>
      <h1 className="section-title">Fortnox Invoice Payment Account Settings</h1>

      {/* Loading Alert */}
      {isLoading && (
        <div className="alert alert-info">
          <span>Loading...</span>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : message.type === 'error' ? 'alert-danger' : 'alert-info'}`}>
          <span>{message.text}</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Site Selection */}
          <div>
            <label className="form-label">Site ID:</label>
            <select
              value={selectedSiteId || ''}
              onChange={(e) => handleSiteChange(parseInt(e.target.value))}
              className="form-input"
              disabled={isLoading}
            >
              {sites.map((site: Site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Settings Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label form-label-required">Invoice Payment Account:</label>
              <input
                type="number"
                value={formData.invoice_payment_account || ''}
                onChange={(e) => handleInputChange('invoice_payment_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="form-label form-label-required">Integration Type:</label>
              <select
                value={formData.payment_service_provider_id || ''}
                onChange={(e) => handleInputChange('payment_service_provider_id', e.target.value)}
                className="form-input"
                disabled={isLoading}
                required
              >
                {paymentServiceProviders.map((provider) => (
                  <option key={provider.value} value={provider.value}>
                    {provider.text}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              disabled={isLoading || saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>

            {formData.id && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={isLoading || deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FortnoxInvoicePaymentAccountSettings;
